/**
 * <pdp-drawer>
 * Fetches a product page and shows it as a bottom sheet drawer.
 * Trigger with: openPdpDrawer('/products/handle')
 */
class PdpDrawer extends HTMLElement {
  connectedCallback() {
    this.innerHTML = `
      <div class="pdp-drawer__backdrop" part="backdrop"></div>
      <div class="pdp-drawer__sheet" role="dialog" aria-modal="true">
        <div class="pdp-drawer__handle"></div>
        <button type="button" class="pdp-drawer__close" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="pdp-drawer__body">
          <div class="pdp-drawer__loading">Loading…</div>
        </div>
      </div>
    `;

    this.backdrop = this.querySelector('.pdp-drawer__backdrop');
    this.sheet = this.querySelector('.pdp-drawer__sheet');
    this.body = this.querySelector('.pdp-drawer__body');
    this.closeBtn = this.querySelector('.pdp-drawer__close');

    this.backdrop.addEventListener('click', () => this.close());
    this.closeBtn.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });
  }

  async open(url) {
    this.classList.add('is-open');
    document.documentElement.classList.add('pdp-drawer-lock');
    this.body.innerHTML = '<div class="pdp-drawer__loading">Loading…</div>';

    requestAnimationFrame(() => this.sheet.classList.add('is-up'));

    try {
      // Try fast path: fetch only the main product section
      const sep = url.includes('?') ? '&' : '?';
      let injected = false;

      try {
        const res = await fetch(`${url}${sep}section_id=main`);
        const html = await res.text();
        if (html && html.trim().length > 50) {
          const wrap = document.createElement('div');
          wrap.innerHTML = html;
          this.body.innerHTML = '';
          this.body.appendChild(wrap);
          this._runScripts(wrap);
          injected = true;
        }
      } catch (e) { /* fall through */ }

      // Fallback: fetch full page, extract main content
      if (!injected) {
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const main = doc.querySelector('#MainContent') || doc.querySelector('main');
        if (main) {
          this.body.innerHTML = '';
          this.body.appendChild(main);
          this._runScripts(main);
          injected = true;
        }
      }

      if (injected) {
        this._trimAfterShare(url);
        this.body.scrollTop = 0;
      } else {
        this.body.innerHTML = '<p class="pdp-drawer__error">Could not load product.</p>';
      }
    } catch (err) {
      this.body.innerHTML = '<p class="pdp-drawer__error">Could not load product.</p>';
    }
  }

  _trimAfterShare(url) {
    const share = this.body.querySelector('.product-addons__share');
    if (share) {
      // Find the top-level row that holds the share button and remove all siblings after it
      let node = share;
      while (node && node.parentElement && node.parentElement !== this.body) {
        node = node.parentElement;
      }
      if (node) {
        while (node.nextSibling) node.nextSibling.remove();
      }
    }

    // Append the View full details link
    const cta = document.createElement('a');
    cta.href = url;
    cta.className = 'pdp-drawer__viewfull';
    cta.innerHTML = 'View full details <span aria-hidden="true">&raquo;</span>';
    this.body.appendChild(cta);
  }

  _runScripts(container) {
    container.querySelectorAll('script').forEach((old) => {
      const s = document.createElement('script');
      if (old.src) {
        s.src = old.src;
      } else {
        s.textContent = old.textContent;
      }
      old.replaceWith(s);
    });
  }

  close() {
    this.sheet.classList.remove('is-up');
    document.documentElement.classList.remove('pdp-drawer-lock');
    setTimeout(() => {
      this.classList.remove('is-open');
      this.body.innerHTML = '';
    }, 280);
  }
}

customElements.define('pdp-drawer', PdpDrawer);

function ensurePdpDrawer() {
  let el = document.querySelector('pdp-drawer');
  if (!el) {
    el = document.createElement('pdp-drawer');
    document.body.appendChild(el);
  }
  return el;
}

window.openPdpDrawer = function (url) {
  ensurePdpDrawer().open(url);
};

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-pdp-drawer-url]');
  if (!trigger) return;

  const url = trigger.getAttribute('data-pdp-drawer-url');
  const w = window.innerWidth;

  // Determine device band
  let allowed;
  if (w <= 749) {
    allowed = trigger.getAttribute('data-drawer-mobile');
  } else if (w <= 989) {
    allowed = trigger.getAttribute('data-drawer-tablet');
  } else {
    allowed = trigger.getAttribute('data-drawer-desktop');
  }

  // If this device is disabled, let the browser follow the link / go to PDP
  if (allowed === 'false') {
    if (trigger.tagName === 'BUTTON') {
      e.preventDefault();
      window.location.href = url;
    }
    return;
  }

  e.preventDefault();
  window.openPdpDrawer(url);
});